import { Link, useNavigate } from "react-router-dom";
import { Paper, Title, Text, Loader, Center, Button, Container, Divider, Group, ScrollArea, Space, Stack, ThemeIcon, Menu, Badge, useMantineTheme, ActionIcon, SimpleGrid, Modal, Image } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconClipboardList, IconPencil, IconDots, IconTrash, IconChevronLeft, IconMapPin, IconTag, IconUser, IconQrcode, IconCopy, IconDownload, IconClipboardPlus, IconBox, IconBuildingStore } from "@tabler/icons-react";
import moment from "moment";
import { useState, useMemo, useEffect } from "react";

import { useAccess } from "../../Auth/auth-functions";
import { AzureADRoles } from "../../Auth/auth-roles";
import { WorkOrderTemplateDto } from "../../models/workordertemplate.models";
import { useGetWorkOrderTemplateQRCodeQuery, useResetWorkOrderTemplateQRCodeQuery } from "../../api/workordertemplate.api";
import { CrudUserDate } from "../Shared/CrudUserDate";
import classes from './WorkOrderTemplateDetailView.module.css';

interface WorkOrderTemplateDetailViewProps {
	template?: WorkOrderTemplateDto | null;
	isLoading?: boolean;
	onEdit?: () => void;
	onDelete?: (template: WorkOrderTemplateDto) => void;
	onClose?: () => void;
}

export const WorkOrderTemplateDetailView = ({ template, isLoading, onEdit, onDelete, onClose }: WorkOrderTemplateDetailViewProps) => {
	const navigate = useNavigate();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const canEditTemplate = useAccess(AzureADRoles.Admin);
	const isRequestor = !useAccess(AzureADRoles.Admin);
	const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);

	const { data: qrCodeBlob } = useGetWorkOrderTemplateQRCodeQuery(template?.WorkOrderTemplateId ?? null, qrCodeModalOpen);
	const { reset } = useResetWorkOrderTemplateQRCodeQuery(template?.WorkOrderTemplateId ?? null);

	const deleteTemplate = () => {
		if (!template) return;

		if (onDelete && template)
			onDelete(template);
	}

	const qrCodeUrl = useMemo(() => {
		if (qrCodeBlob) {
			return window.URL.createObjectURL(qrCodeBlob);
		}
		return null;
	}, [qrCodeBlob]);

	useEffect(() => {
		return () => {
			if (qrCodeUrl) {
				window.URL.revokeObjectURL(qrCodeUrl);
				reset();
			}
		};
	}, [qrCodeUrl, reset]);

	const handleCreateWorkOrder = () => {
		navigate(`/workorders/${isRequestor ? 'new-request' : 'new'}/template/${template?.WorkOrderTemplateId}`);
	}

	const handleCopyQRCode = async () => {
		if (!qrCodeBlob) return;
		try {
			const item = new ClipboardItem({ 'image/png': qrCodeBlob });
			await navigator.clipboard.write([item]);
		} catch (err) {
			console.error('Failed to copy QR code:', err);
		}
	};

	const handleDownloadQRCode = () => {
		if (!qrCodeUrl || !template) return;
		try {
			const a = document.createElement('a');
			a.href = qrCodeUrl;
			a.download = `${template.Title} QR Code.png`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		} catch (err) {
			console.error('Failed to download QR code:', err);
		}
	};

	if (isLoading) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)' }}>
				<Center>
					<Loader size="sm" mt="lg" />
				</Center>
			</ScrollArea>
		</Paper>
	if (!template) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 160px)' }}>
				<Space h="md" />
				<Stack justify="center" align="center" gap="md" pt="lg">
					<ThemeIcon size={100} radius={100} variant="light" color="gray">
						<IconClipboardList size={60} />
					</ThemeIcon>
					<Text fz="lg" fw="500" c="dimmed">Select a template to view details</Text>
				</Stack>
				<Space h="md" />
			</ScrollArea>
		</Paper>

	return <>
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 160px)' }}>
				<Stack gap={0} mb="md" justify="center" className={classes['header']}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<ActionIcon variant="light" radius="xl" onClick={onClose} hiddenFrom="sm">
								<IconChevronLeft />
							</ActionIcon>
							<Stack gap={0}>
								<Title order={isMobile ? 6 : 3}>{template?.Title}</Title>
								<Text fz="xs" c="dimmed">Template created on {moment(template?.Created).format("MMMM D, YYYY")}</Text>
							</Stack>
						</Group>
						<Group gap="xs">
							<Button.Group mr={-10}>
								{!template.Deleted && <>
									{isRequestor &&
									<Button onClick={handleCreateWorkOrder} variant="subtle" radius="xl" leftSection={<IconClipboardPlus stroke={1.5} />}>Create Work Order</Button>
									}
									{!isRequestor && <>
									{!isMobile &&
									<Button onClick={onEdit} disabled={!canEditTemplate} variant="subtle" radius="xl" leftSection={<IconPencil stroke={1.5} />}>Edit</Button>
									}
									{isMobile &&
									<Button onClick={onEdit} disabled={!canEditTemplate} variant="subtle" radius="xl" px="sm"><IconPencil stroke={1.5} /></Button>
									}
									<Divider orientation="vertical" />
									<Button onClick={() => setQrCodeModalOpen(true)} disabled={!canEditTemplate} variant="subtle" radius="xl" px="sm"><IconQrcode stroke={1.5} size={20} /></Button>
									<Divider orientation="vertical" />
									<Menu position="bottom-end" shadow="md" radius="lg">
										<Menu.Target>
											<Button disabled={!canEditTemplate} variant="subtle" radius="xl" px="sm"><IconDots size={20} /></Button>
										</Menu.Target>
										<Menu.Dropdown>
											<Menu.Item onClick={handleCreateWorkOrder} leftSection={<IconClipboardPlus stroke={1.5} />}>Create Work Order</Menu.Item>
											<Menu.Item onClick={() => setQrCodeModalOpen(true)} leftSection={<IconQrcode stroke={1.5} />}>View QR Code</Menu.Item>
											<Menu.Divider />
											<Menu.Item onClick={deleteTemplate} color="red" leftSection={<IconTrash stroke={1.5} />} fw="bold">Delete</Menu.Item>
										</Menu.Dropdown>
									</Menu>
									</>}
								</>}
							</Button.Group>
						</Group>
					</Group>
				</Stack>
				<Container px="20" pb="xl">
					{template.Description && <>
						<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Description</Text>
						<Text fz="sm">{template.Description}</Text>
					</>}

					<SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" mt="lg">
						<Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Priority</Text>
							<Badge size="lg" color={template?.Priority?.Colour} variant="dot" radius="sm">{template?.Priority?.ShortTitle}</Badge>
						</Stack>
						<Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Due Days</Text>
							<Text fz="sm" fw="500">{template.DueDays ? `${template.DueDays} days` : "Not set"}</Text>
						</Stack>
					</SimpleGrid>

					<Divider my="md" />

					<Stack gap="xs" mt="md">
						<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Assigned To</Text>
						<Group gap="xs">
							{template.AssignedTo?.Teams?.length ? template.AssignedTo.Teams.map(team => (
								<Link key={team.TeamId} to={{ pathname: `/teams/${team.TeamId}` }} className={classes['link']}>
									<Badge size="lg" fz="sm" radius="sm" leftSection={<IconTag size={14} />}>{team.Name}</Badge>
								</Link>
							)) : null}
							{template.AssignedTo?.Users?.length ? template.AssignedTo.Users.map(user => (
								<Link key={user.UserId} to={{ pathname: `/users/${user.UserId}` }} className={classes['link']}>
									<Badge size="lg" fz="sm" radius="sm" leftSection={<IconUser size={14} />}>{user.Name}</Badge>
								</Link>
							)) : null}
							{!template.AssignedTo?.Users?.length && !template.AssignedTo?.Teams?.length && (
								<Text fz="sm" c="dimmed">No assignments</Text>
							)}
						</Group>
					</Stack>

					<Divider my="md" />

					<SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" mt="lg">
						<Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Location</Text>
							<Group gap="sm">
								<Link to={{ pathname: `/locations/${template?.Location.LocationId}` }} className={classes['link']}>
									<Badge size="lg" fz="sm" radius="sm" leftSection={<IconMapPin size={14} />}>{template?.Location.Title}</Badge>
								</Link>
							</Group>
						</Stack>
						<Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Categories</Text>
							<Group gap="sm">
								{template.Categories?.length > 0 ? template.Categories.map(category => (
									<Link key={category.CategoryId} to={{ pathname: `/categories/${category.CategoryId}` }} className={classes['link']}>
										<Badge size="lg" fz="sm" radius="sm" leftSection={<IconTag size={14} />}>{category.Title}</Badge>
									</Link>
								)) : (
									<Text fz="sm" c="dimmed">No categories</Text>
								)}
							</Group>
						</Stack>
					</SimpleGrid>

					<Divider my="md" />

					<SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
						<Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Assets</Text>
							<Group gap="xs">
								{template.Assets?.length > 0 ? template.Assets.map(asset => (
									<Link key={asset.AssetId} to={{ pathname: `/assets/${asset.AssetId}` }} className={classes['link']}>
										<Badge size="lg" fz="sm" radius="sm" leftSection={<IconBox size={14} />}>{asset.Name}</Badge>
									</Link>
								)) : (
									<Text fz="sm" c="dimmed">No assets</Text>
								)}
							</Group>
						</Stack>
						<Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Vendors</Text>
							<Group gap="xs">
								{template.Vendors?.length > 0 ? template.Vendors.map(vendor => (
									<Link key={vendor.VendorId} to={{ pathname: `/vendors/${vendor.VendorId}` }} className={classes['link']}>
										<Badge size="lg" fz="sm" radius="sm" leftSection={<IconBuildingStore size={14} />}>{vendor.Name}</Badge>
									</Link>
								)) : (
									<Text fz="sm" c="dimmed">No vendors</Text>
								)}
							</Group>
						</Stack>
					</SimpleGrid>				

					<Divider my="md" />

					<SimpleGrid cols={isMobile ? 1 : 2}>
						<CrudUserDate label="Created" userId={template.CreatedBy.UserId} userName={template.CreatedBy.Name} date={template.Created} />
						{template.UpdatedBy && !template.Deleted && <>
							<CrudUserDate label="Updated" userId={template.UpdatedBy.UserId} userName={template.UpdatedBy.Name} date={template.Updated ?? null} />
						</>
						}
						{template.DeletedBy && <>
							<CrudUserDate label="Deleted" userId={template.DeletedBy.UserId} userName={template.DeletedBy.Name} date={template.Deleted ?? null} />
						</>
						}
					</SimpleGrid>
				</Container>
			</ScrollArea>
		</Paper>

		<Modal
			opened={qrCodeModalOpen}
			onClose={() => setQrCodeModalOpen(false)}
			title={<Text fz="xl" fw="bold">Work Order Template QR Code</Text>}
			centered
			size="sm"
			radius="lg"
		>
			<Stack gap="0">
				<Text size="sm" c="dimmed" ta="center" mb="xs">
					This QR code can be scanned to quickly create a work order based on this template.
				</Text>
				<Center>
					{qrCodeUrl ? (
						<Image
							src={qrCodeUrl}
							alt={`QR Code for ${template.Title}`}
							fit="contain"
							w={250}
							h={250}
						/>
					) : (
						<Loader size="md" />
					)}
				</Center>
				<Text size="sm" c="dimmed" ta="center" mt="xs">
					{template.Title}
				</Text>
				<Group grow mt="sm">
					<Button
						variant="default"
						radius="xl"
						leftSection={<IconCopy size={16} />}
						disabled={!qrCodeBlob}
						onClick={handleCopyQRCode}
					>
						Copy
					</Button>
					<Button
						variant="filled"
						radius="xl"
						leftSection={<IconDownload size={16} />}
						disabled={!qrCodeUrl}
						onClick={handleDownloadQRCode}
					>
						Download
					</Button>
				</Group>
			</Stack>
		</Modal>
	</>
}
